import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';

// Damage state type (shared with Unit and Structure)
export type DamageState = 'none' | 'light' | 'medium' | 'heavy' | 'critical';

// Default damage thresholds (HP percentage)
export interface DamageThresholds {
  light: number;    // default: 75 - triggers when HP <= 75%
  medium: number;   // default: 50 - triggers when HP <= 50%
  heavy: number;    // default: 25 - triggers when HP <= 25%
  critical: number; // default: 10 - triggers when HP <= 10%
}

export const DEFAULT_DAMAGE_THRESHOLDS: DamageThresholds = {
  light: 75,
  medium: 50,
  heavy: 25,
  critical: 10
};

export interface HealthConfig extends BehaviorConfig {
  hp?: number;
  maxHp?: number;
  invulnerabilityTime?: number;
  damageThresholds?: Partial<DamageThresholds>;
  /**
   * Use C3-style prorate damage system.
   * Instead of fixed percentage thresholds, calculates frame directly
   * based on: frame = totalFrames * (1 - currentHP/maxHP)
   * This uses ALL available frames proportionally.
   */
  useProRate?: boolean;
  /** Total frames for prorate calculation (from sprite animation) */
  totalFrames?: number;
}

export interface DamageEvent {
  entity: Entity;
  damage: number;
  remainingHp: number;
}

export interface DeathEvent {
  entity: Entity;
}

/**
 * HealthBehavior - Manages HP, damage, and death
 *
 * Used by: Unit, Structure, Boss
 *
 * Events emitted:
 * - 'entityDamaged': { entity, damage, remainingHp }
 * - 'entityDied': { entity }
 */
export class HealthBehavior extends Behavior {
  public hp: number;
  public maxHp: number;
  public invulnerable: boolean = false;
  public invulnerabilityTime: number;
  public damageThresholds: DamageThresholds;
  public currentDamageState: DamageState = 'none';

  /** Use C3-style prorate system instead of fixed thresholds */
  public useProRate: boolean;
  /** Total frames for prorate calculation */
  public totalFrames: number;
  /** Current calculated damage frame (for prorate mode). Starts at -1 to force initial update */
  public currentDamageFrame: number = -1;

  private invulnerabilityTimer?: Phaser.Time.TimerEvent;

  constructor(entity: Entity, config: HealthConfig = {}) {
    super(entity, config);

    const entityName = (entity as Entity & { texture?: { key: string } }).texture?.key ?? entity.entityType ?? 'unknown';

    // Validate required config - hp and maxHp are REQUIRED
    const missing: string[] = [];
    if (config.hp === undefined) missing.push('hp');
    if (config.maxHp === undefined) missing.push('maxHp');

    if (missing.length > 0) {
      console.error(`[HealthBehavior] Missing required config for entity "${entityName}":`, missing);
      console.error('[HealthBehavior] Stack trace:', new Error().stack);
      this.enabled = false;
      // Assign dummy values only to avoid undefined (but the behavior is disabled)
      this.maxHp = 0;
      this.hp = 0;
      this.invulnerabilityTime = 0;
      this.damageThresholds = DEFAULT_DAMAGE_THRESHOLDS;
      this.totalFrames = 1;
      this.useProRate = false;
      return;
    }

    this.maxHp = config.maxHp;
    this.hp = config.hp;
    this.invulnerabilityTime = config.invulnerabilityTime ?? 0;
    this.damageThresholds = {
      ...DEFAULT_DAMAGE_THRESHOLDS,
      ...config.damageThresholds
    };
    this.totalFrames = config.totalFrames ?? 1;
    // Auto-activate prorate mode if totalFrames > 1 (C3-style proportional damage)
    this.useProRate = config.useProRate ?? (this.totalFrames > 1);

    // DEBUG: Log health initialization
    // console.log(`[HealthBehavior] Init for ${entityName}:`, {
    //   hp: this.hp,
    //   maxHp: this.maxHp,
    //   totalFrames: this.totalFrames,
    //   useProRate: this.useProRate,
    //   configTotalFrames: config.totalFrames,
    //   configBehaviorsHealth: config
    // });
  }

  /**
   * Deal damage to this entity
   * @returns true if entity died, false otherwise
   */
  takeDamage(amount: number): boolean {
    if (!this.enabled) return false;
    if (this.invulnerable || this.hp <= 0) {
      return false;
    }

    const previousHp = this.hp;
    this.hp -= amount;

    // DEBUG: Log damage taken
    // const textureKey = (this.entity as Entity & { texture?: { key: string } }).texture?.key ?? 'unknown';
    // console.log(`[HealthBehavior] takeDamage for ${textureKey}:`, {
    //   damage: amount,
    //   previousHp,
    //   newHp: this.hp,
    //   maxHp: this.maxHp,
    //   totalFrames: this.totalFrames,
    //   useProRate: this.useProRate
    // });

    // Update damage state based on new HP
    this.updateDamageState();

    // Call entity callback if exists
    if (typeof (this.entity as Entity & { onDamage?: (amount: number) => void }).onDamage === 'function') {
      (this.entity as Entity & { onDamage: (amount: number) => void }).onDamage(amount);
    }

    // Emit global event
    this.scene.events.emit('entityDamaged', {
      entity: this.entity,
      damage: amount,
      remainingHp: this.hp
    } as DamageEvent);

    if (this.hp <= 0) {
      this.hp = 0;
      this.die();
      return true;
    }

    // Apply invulnerability if configured
    if (this.invulnerabilityTime > 0) {
      this.setInvulnerable(this.invulnerabilityTime);
    }

    return false;
  }

  /**
   * Heal this entity
   */
  heal(amount: number): void {
    if (!this.enabled) return;
    const oldHp = this.hp;
    this.hp = Math.min(this.hp + amount, this.maxHp);

    // Update damage state (healing can reduce damage level)
    this.updateDamageState();

    if (typeof (this.entity as Entity & { onHeal?: (amount: number) => void }).onHeal === 'function') {
      (this.entity as Entity & { onHeal: (amount: number) => void }).onHeal(this.hp - oldHp);
    }
  }

  /**
   * Calculate and update damage state based on current HP
   * Uses prorate mode (C3-style) or fixed thresholds depending on config.
   */
  private updateDamageState(): void {
    if (this.useProRate) {
      this.updateProRateDamage();
    } else {
      this.updateThresholdDamage();
    }
  }

  /**
   * C3-style prorate damage: calculates frame directly based on HP ratio
   * Formula: frame = totalFrames * (1 - currentHP/maxHP)
   * This uses ALL available frames proportionally.
   */
  private updateProRateDamage(): void {
    const hpRatio = this.hp / this.maxHp;
    // Calculate frame: more damage = higher frame number
    // frame = totalFrames - (totalFrames/maxHP * currentHP)
    // Simplified: frame = totalFrames * (1 - hpRatio)
    const newFrame = Math.floor(this.totalFrames * (1 - hpRatio));
    // Clamp to valid range (0 to totalFrames-1)
    const clampedFrame = Math.max(0, Math.min(newFrame, this.totalFrames - 1));

    // DEBUG: Log frame calculation
    // console.log(`[HealthBehavior] ProRate calc for ${(this.entity as Entity & { texture?: { key: string } }).texture?.key ?? 'unknown'}:`, {
    //   hp: this.hp,
    //   maxHp: this.maxHp,
    //   hpRatio: hpRatio.toFixed(3),
    //   totalFrames: this.totalFrames,
    //   rawFrame: newFrame,
    //   clampedFrame,
    //   previousFrame: this.currentDamageFrame,
    //   willUpdate: clampedFrame !== this.currentDamageFrame
    // });

    if (clampedFrame !== this.currentDamageFrame) {
      this.currentDamageFrame = clampedFrame;

      // Call entity's setDamageFrame if it exists (Structure has this)
      if (typeof (this.entity as Entity & { setDamageFrame?: (frame: number) => void }).setDamageFrame === 'function') {
        (this.entity as Entity & { setDamageFrame: (frame: number) => void }).setDamageFrame(clampedFrame);
      }

      // Emit event for external listeners
      this.scene.events.emit('entityDamageFrameChanged', {
        entity: this.entity,
        damageFrame: clampedFrame,
        hpRatio: hpRatio
      });
    }
  }

  /**
   * Fixed threshold damage: uses percentage thresholds for damage states
   * (Original system - kept for backwards compatibility with Units)
   */
  private updateThresholdDamage(): void {
    const percent = this.getHpPercent();
    const t = this.damageThresholds;

    let newState: DamageState = 'none';
    if (percent <= t.critical) {
      newState = 'critical';
    } else if (percent <= t.heavy) {
      newState = 'heavy';
    } else if (percent <= t.medium) {
      newState = 'medium';
    } else if (percent <= t.light) {
      newState = 'light';
    }

    // Only update if state changed
    if (newState !== this.currentDamageState) {
      this.currentDamageState = newState;

      // Call entity's setDamage if it exists (Unit and Structure have this)
      if (typeof (this.entity as Entity & { setDamage?: (state: DamageState) => void }).setDamage === 'function') {
        (this.entity as Entity & { setDamage: (state: DamageState) => void }).setDamage(newState);
      }

      // Emit event for external listeners
      this.scene.events.emit('entityDamageStateChanged', {
        entity: this.entity,
        damageState: newState,
        hpPercent: percent
      });
    }
  }

  /**
   * Set HP directly (for initialization or special cases)
   */
  setHp(hp: number): void {
    this.hp = Math.max(0, Math.min(hp, this.maxHp));
  }

  /**
   * Set max HP
   */
  setMaxHp(maxHp: number, healToFull: boolean = false): void {
    this.maxHp = maxHp;
    if (healToFull) {
      this.hp = maxHp;
    } else {
      this.hp = Math.min(this.hp, maxHp);
    }
  }

  /**
   * Configure prorate mode (C3-style proportional damage)
   * @param totalFrames Total number of damage frames available
   */
  setProRateMode(totalFrames: number): void {
    this.useProRate = true;
    this.totalFrames = totalFrames;
    // Update immediately to show correct frame
    this.updateDamageState();
  }

  /**
   * Get the current damage frame (for prorate mode)
   */
  getDamageFrame(): number {
    return this.currentDamageFrame;
  }

  /**
   * Get HP as percentage (0-100)
   */
  getHpPercent(): number {
    return (this.hp / this.maxHp) * 100;
  }

  /**
   * Check if entity is alive
   */
  isAlive(): boolean {
    return this.hp > 0;
  }

  /**
   * Check if entity is at full health
   */
  isFullHealth(): boolean {
    return this.hp >= this.maxHp;
  }

  /**
   * Make entity invulnerable for a duration
   */
  setInvulnerable(duration: number): void {
    this.invulnerable = true;

    // Clear existing timer
    if (this.invulnerabilityTimer) {
      this.invulnerabilityTimer.destroy();
    }

    this.invulnerabilityTimer = this.scene.time.delayedCall(duration, () => {
      this.invulnerable = false;
    });
  }

  /**
   * Handle entity death
   */
  private die(): void {
    // Call entity callback if exists
    if (typeof (this.entity as Entity & { onDeath?: () => void }).onDeath === 'function') {
      (this.entity as Entity & { onDeath: () => void }).onDeath();
    }

    // Emit global event
    this.scene.events.emit('entityDied', {
      entity: this.entity
    } as DeathEvent);

    // Call handleDeath for custom death logic
    if (typeof (this.entity as Entity & { handleDeath?: () => void }).handleDeath === 'function') {
      (this.entity as Entity & { handleDeath: () => void }).handleDeath();
    }
  }

  /**
   * Clean up timers
   */
  destroy(): void {
    if (this.invulnerabilityTimer) {
      this.invulnerabilityTimer.destroy();
    }
    super.destroy();
  }
}
